import * as React from 'react';
import { Check, ChevronsUpDown, Building, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface CustomerOption {
  id: string;
  type: 'business' | 'person';
  name: string;
  address?: string | null;
  email?: string | null;
}

interface CustomerSelectProps {
  customers: CustomerOption[];
  value?: string | null;
  customerType?: 'business' | 'person' | null;
  onChange: (id: string, type: 'business' | 'person', customer: CustomerOption) => void;
  onClear: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
}

export function CustomerSelect({
  customers,
  value,
  customerType,
  onChange,
  onClear,
  placeholder = 'Search customers...',
  disabled = false,
  className,
  hasError = false,
}: CustomerSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selectedCustomer = React.useMemo(() => {
    if (!value) return null;
    return customers.find(c => c.id === value && c.type === customerType) || null;
  }, [customers, value, customerType]);

  const dropdownContent = (
    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
      <Command>
        <CommandInput placeholder="Type to filter..." />
        <CommandList>
          <CommandEmpty>No customers found.</CommandEmpty>
          <CommandGroup>
            {customers.map((customer) => (
              <CommandItem
                key={`${customer.type}-${customer.id}`}
                value={`${customer.name} ${customer.address || ''}`}
                onSelect={() => {
                  onChange(customer.id, customer.type, customer);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 flex-shrink-0',
                    value === customer.id && customerType === customer.type
                      ? 'opacity-100'
                      : 'opacity-0',
                  )}
                />
                <div className="flex items-start gap-2 min-w-0">
                  {customer.type === 'business' ? (
                    <Building className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{customer.name}</p>
                    {customer.address && (
                      <p className="text-xs text-muted-foreground truncate">{customer.address}</p>
                    )}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal group hover:text-white',
            hasError && 'border-destructive',
            className,
          )}
          disabled={disabled}
        >
          {selectedCustomer ? (
            <span className="flex items-center gap-2 truncate">
              {selectedCustomer.type === 'business' ? (
                <Building className="h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-white" />
              ) : (
                <User className="h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-white" />
              )}
              <span className="truncate">{selectedCustomer.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground group-hover:text-white">Customer</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 group-hover:text-white group-hover:opacity-100" />
        </Button>
      </PopoverTrigger>
      {dropdownContent}
    </Popover>
  );
}
